import { useEffect, useRef, type ReactNode } from 'react'

/**
 * A modal dialog on the platform's own <dialog> element.
 *
 * showModal() gives what a hand-built overlay has to imitate and usually gets
 * partly wrong: the rest of the page becomes inert, focus stays inside, Escape
 * closes it, and focus goes back to whatever opened it.
 */
export function Dialog({
  open,
  onClose,
  labelledBy,
  className,
  children,
}: {
  open: boolean
  onClose: () => void
  /** Id of the element that names the dialog. */
  labelledBy: string
  className?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  // Whether the press began on the backdrop: a text selection dragged out of
  // the dialog ends with a click there too, and must not close it.
  const pressedBackdrop = useRef(false)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    // A click on the backdrop closes it; Escape already does so natively.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      className={`dialog${className ? ` ${className}` : ''}`}
      aria-labelledby={labelledBy}
      // Escape, or anything else that closes it, keeps React's state in step.
      onClose={onClose}
      onPointerDown={(event) => {
        pressedBackdrop.current = event.target === ref.current
      }}
      onClick={(event) => {
        // A click on the dialog element itself, not its contents, landed on
        // the backdrop.
        if (event.target === ref.current && pressedBackdrop.current) onClose()
        pressedBackdrop.current = false
      }}
    >
      {/* The dialog itself has no padding, so a click on it can only be the backdrop. */}
      {open && <div className="dialog-inner">{children}</div>}
    </dialog>
  )
}
